import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from 'icons';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { courseConst, ITeacher, SocialMedia } from 'modules/courses';
import {
  Avatar,
  Banner,
  FollowAndShare,
  Followers,
  getTeacher,
  getTeacherTests,
  Tabs,
  useTeacherBySlug
} from 'modules/teacher-detail';

import { NextHead } from 'components';

const Index = () => {
  const t = useTranslations();
  const { query, back } = useRouter();
  const { teacher, isLoading } = useTeacherBySlug(query.teacher as string);

  return (
    <>
      <NextHead
        banner={teacher?.image?.large}
        title={t('meta.teacher_detail.title', { teacher: teacher?.full_name as string })}
        description={t('meta.teacher_detail.description', { teacher: teacher?.full_name as string })}
      />
      <div className="max-w-screen mx-auto my-5 min-h-screen p-4">
        <div
          onClick={() => back()}
          className="mb-4 inline-flex cursor-pointer items-center justify-start gap-2 font-semibold"
        >
          <ArrowLeftIcon /> {t('common.ortga_qaytish')}
        </div>
        <Banner banner={teacher?.image?.large} alt={teacher?.full_name} />
        <div className="mt-4 grid grid-cols-1 gap-y-4 p-2 lg:grid-cols-4 lg:gap-4 lg:gap-y-0 lg:p-0">
          <div className="lg:sticky lg:top-4 lg:block">
            <div className="flex flex-col gap-3 rounded-xl bg-white p-3 sm:flex-row lg:flex-col">
              <div className="w-full">
                <Avatar username={teacher?.user_name} image={teacher?.image?.small} name={teacher?.full_name} />
                <Followers followers={teacher?.followers_count || 0} students={teacher?.students_count || 0} />
                <FollowAndShare teacherId={teacher?.id} isFollowed={teacher?.is_follow} slug={teacher?.slug} />
              </div>
              <div className="w-full">
                <SocialMedia isTeacherPage contacts={teacher?.contacts} />
              </div>
            </div>
          </div>
          <div className="space-y-4 sm:col-span-3">
            <Tabs teacher={teacher} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [courseConst.queryKeys.TEACHER_BY_SLUG, params?.teacher as string],
    queryFn: getTeacher(params?.teacher as string)
  });

  const teacher: ITeacher | undefined = queryClient.getQueryData([
    courseConst.queryKeys.TEACHER_BY_SLUG,
    params?.teacher as string
  ]);

  await queryClient.prefetchQuery({
    queryKey: [courseConst.queryKeys.TEACHER_TESTS, teacher?.id],
    queryFn: getTeacherTests(teacher?.id)
  });

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      messages: (await import(`../../messages/${locale}.json`)).default
    },
    notFound: !teacher
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
